import { Dialog } from "@astryxdesign/core/Dialog";
import { Layout, LayoutContent, LayoutFooter, LayoutHeader } from "@astryxdesign/core/Layout";
import { HStack } from "@astryxdesign/core/HStack";
import { VStack } from "@astryxdesign/core/VStack";
import { Button } from "@astryxdesign/core/Button";
import { IconButton } from "@astryxdesign/core/IconButton";
import { StatusDot } from "@astryxdesign/core/StatusDot";
import { X } from "lucide-react";
import { hasPermission, type Camera } from "../../lib/api";
import { useAuth } from "../auth/AuthProvider";
import type { HealthState } from "../health/useHealthEvents";
import type { StreamSlots } from "../stream/streamSlots";
import type { TileCommands } from "../stream/tileCommands";
import { isServerLive } from "../stream/serverHealth";
import { VideoTile } from "../stream/VideoTile";
import { PtzPad } from "../ptz/PtzPad";
import { usePtzControl } from "../ptz/usePtzControl";
import { usePtzPresets } from "../ptz/usePtzPresets";
import { useIsNarrowViewport } from "../../lib/responsive";

// A constant, not `focus:${cameraId}` or `camera.id` — this is load-bearing,
// not a style choice. streamSlots.ts's `acquire` is a free no-op for a
// tileId a caller already holds, and `release` is unconditional; if this
// tile ever shared a tileId with the grid's own tile for the same camera,
// whichever side tears down first would free a slot the other side still
// holds, permanently drifting the shared budget upward. A single constant
// means the focus tile can never hold more than one slot no matter how fast
// the filmstrip below is clicked through.
export const FOCUS_TILE_ID = "__focus__";

interface FocusDialogProps {
  /** The camera currently focused, or null when the dialog should be
   *  closed. LiveGridPage is responsible for clearing this if the camera
   *  leaves scope (disabled, deleted, or scrolled out of the selected
   *  project) — see its own effect for that. */
  focusedCamera: Camera | null;
  /** Already project-scoped by the caller — every camera visible in the
   *  current grid, for the filmstrip. */
  cameras: Camera[];
  onFocusChange: (cameraId: string | null) => void;
  /** The SAME shared StreamSlots instance the grid's tiles use. Correctness
   *  depends on this: LiveGridPage suspends the grid tile for the focused
   *  camera (enabled=false) in the same state update that opens this
   *  dialog, releasing its slot before this one acquires FOCUS_TILE_ID — so
   *  the total held slots never exceeds what it was before focus mode
   *  opened. A separate/unlimited slot pool here would let the two tiles
   *  double-stream the same camera (two leases, doubled bandwidth, a
   *  viewer_count that lies about how many people are actually watching). */
  slots: StreamSlots;
  /** Same shared command channel the grid's tiles listen on — see
   *  LiveGridPage's own comment on why this must be passed here too, not a
   *  separate instance: a camera evicted while it's open in this dialog
   *  needs to shut down cleanly here, the one view an operator is watching
   *  hardest, not just on the grid tile suspended behind it. */
  commands: TileCommands;
  health: HealthState;
}

// The focused camera's own tile, plus its PTZ pad when the camera has a
// mount and the viewer is allowed to drive it.
//
// A separate component because usePtzControl is a hook: FocusDialog renders
// nothing at all when no camera is focused, so calling it up there would
// either break the rules of hooks or keep a control alive for a camera that
// is no longer on screen. Down here, mounting is the signal that this camera
// is focused and unmounting is the signal to stop it — which is exactly the
// teardown guarantee a moving camera needs.
function FocusTile({
  camera,
  slots,
  commands,
  health,
}: {
  camera: Camera;
  slots: StreamSlots;
  commands: TileCommands;
  health: HealthState;
}) {
  const { user } = useAuth();
  // supports_ptz, not ptz_enabled: the backend already folded "does this
  // vendor have a control adapter" into it, so this cannot drift from what
  // the server will actually accept. The permission check is UI gating only
  // — every command is re-authorized server-side regardless.
  const canControl = camera.supports_ptz && hasPermission(user, "cameras.control");
  const control = usePtzControl(camera.id, canControl);
  const { presets } = usePtzPresets(camera.id, canControl);

  // A 16:9 tile on a portrait phone is about a fifth of the screen. Laying
  // the pad over it covers most of the picture you are aiming by — and the
  // thing being aimed is a camera on a live site, so driving it blind is the
  // one thing the pad must not make easy. Below the video it uses space the
  // portrait layout had spare anyway, and nothing overlaps.
  const isNarrow = useIsNarrowViewport();
  // Matches VideoTile's own gate for the overlay, so the pad does not appear
  // live-looking while the stream is still coming up.
  const isLive = isServerLive(health.byCamera.get(camera.id));

  const tile = (
    <VideoTile
      camera={camera}
      slots={slots}
      commands={commands}
      tileId={FOCUS_TILE_ID}
      enabled
      // FOCUS_TILE_ID is a fresh tile from useHlsStream's point of view, so
      // without this it opens idle even when the camera is already running
      // for everyone else on the grid — the operator would click a live
      // tile to see it bigger and land on "Not watching". `isLive` (above)
      // is exactly the "somebody is already watching this" signal
      // `autoStart` exists for: joining costs nothing, since the server
      // shares one RTSP pull per camera across every viewer.
      autoStart={isLive}
      health={health.byCamera.get(camera.id)}
      isFeedLive={health.phase === "live"}
      overlayControls={canControl && !isNarrow ? <PtzPad control={control} presets={presets} /> : undefined}
    />
  );

  if (!canControl || !isNarrow) return tile;

  return (
    // No hAlign here: VStack's default stretches children, which is what keeps
    // the tile at full width. Centring the stack instead shrinks every child
    // to its own content, and the video collapsed to about a third of the
    // screen. The pad is centred on its own row instead.
    <VStack gap={3}>
      {tile}
      <HStack justify="center">
        <PtzPad control={control} presets={presets} isDisabled={!isLive} />
      </HStack>
    </VStack>
  );
}

// Click a tile to open it large, with a filmstrip of the other cameras along
// the bottom to switch focus — see FOCUS_TILE_ID's comment and LiveGridPage
// for how this avoids the slot/lease hazards a naive "just render another
// VideoTile" implementation would hit.
export function FocusDialog({ focusedCamera, cameras, onFocusChange, slots, commands, health }: FocusDialogProps) {
  const isOpen = focusedCamera !== null;

  return (
    <Dialog isOpen={isOpen} onOpenChange={(open) => !open && onFocusChange(null)} variant="fullscreen" purpose="info">
      {/* Astryx's Dialog renders `children` unconditionally on the modal
          path regardless of `isOpen` (only the isInline path early-returns
          when closed) — without this guard, a "closed" dialog would keep a
          VideoTile mounted invisibly, holding a slot and renewing a lease
          for the rest of the session. Conditionally rendering the tile
          itself, not just toggling isOpen, is what makes this safe. */}
      {focusedCamera && (
        <Layout
          height="fill"
          header={
            <LayoutHeader hasDivider padding={3}>
              {/* No camera name here — the tile immediately below shows its
                  own (name, location, and live status together) in its
                  header, and repeating it 40px above in this dialog's own
                  themed chrome was the same fact stated twice. */}
              <HStack justify="end" vAlign="center">
                <IconButton
                  label="Close fullscreen view"
                  tooltip="Close"
                  icon={<X />}
                  variant="ghost"
                  onClick={() => onFocusChange(null)}
                />
              </HStack>
            </LayoutHeader>
          }
          content={
            <LayoutContent padding={4} isScrollable>
              {/* Centred rather than top-aligned: a 16:9 tile can never fill
                  a portrait viewport, so the leftover height is a given — the
                  choice is only whether it all pools underneath the video or
                  splits evenly around it. */}
              {/* minHeight rather than height="fill": LayoutContent is a
                  block element, so a percentage height on the child resolves
                  against nothing and the stack shrinks to its content —
                  measured at 387px inside a 702px parent, with justify:center
                  correctly applied and no free space to centre within. */}
              <VStack justify="center" gap={0} style={{ minHeight: "100%" }}>
                {/* Keyed by camera so switching focus through the filmstrip
                    remounts it. That is what makes FocusTile's PTZ hook tear
                    down — and therefore stop the previous camera — rather than
                    carrying a held move across to a different device. */}
                <FocusTile key={focusedCamera.id} camera={focusedCamera} slots={slots} commands={commands} health={health} />
              </VStack>
            </LayoutContent>
          }
          footer={
            cameras.length > 1 ? (
              <LayoutFooter hasDivider padding={2}>
                <HStack gap={2} isScrollable>
                  {cameras.map((camera) => {
                    const isLive = isServerLive(health.byCamera.get(camera.id));
                    return (
                      <Button
                        key={camera.id}
                        label={camera.name}
                        variant={camera.id === focusedCamera.id ? "secondary" : "ghost"}
                        size="sm"
                        icon={
                          <StatusDot
                            variant={isLive ? "success" : camera.enabled ? "neutral" : "error"}
                            label={isLive ? "Live" : "Not live"}
                            isPulsing={isLive}
                          />
                        }
                        onClick={() => onFocusChange(camera.id)}
                      />
                    );
                  })}
                </HStack>
              </LayoutFooter>
            ) : undefined
          }
        />
      )}
    </Dialog>
  );
}
